import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileText, FolderKanban, MessageSquareQuote, UserRound, LogOut, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const tabs = [
  { to: "/admin", label: "Posts", Icon: FileText, end: true, testId: "admin-tab-posts" },
  { to: "/admin/projects", label: "Projects", Icon: FolderKanban, testId: "admin-tab-projects" },
  { to: "/admin/testimonials", label: "Testimonials", Icon: MessageSquareQuote, testId: "admin-tab-testimonials" },
  { to: "/admin/cv", label: "CV", Icon: UserRound, testId: "admin-tab-cv" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    navigate("/");
    await logout();
    toast.success("Signed out.");
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="admin-layout">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs sm:text-sm font-medium uppercase tracking-[0.2em] text-amber-700">Admin</p>
          <h1 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-stone-900" data-testid="admin-heading">
            Manage your site
          </h1>
        </div>
        <div className="flex items-center gap-3 text-sm text-stone-500">
          <span data-testid="admin-signed-in-as">{user?.email}</span>
          <button
            type="button"
            onClick={handleLogout}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white px-3 py-1.5 text-xs font-semibold text-stone-700 transition-colors hover:border-stone-900"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </div>

      <nav className="mt-10 flex flex-wrap gap-2 border-b border-stone-200 pb-px" data-testid="admin-tabs">
        {tabs.map(({ to, label, Icon, end, testId }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={testId}
            className={({ isActive }) =>
              `inline-flex items-center gap-2 rounded-t-xl px-4 py-2.5 text-sm font-semibold transition-colors -mb-px border-b-2 ${
                isActive ? "border-amber-600 text-stone-900" : "border-transparent text-stone-500 hover:text-stone-900"
              }`
            }
          >
            <Icon className="h-4 w-4" />
            {label}
          </NavLink>
        ))}
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto inline-flex items-center gap-1.5 px-2 py-2.5 text-xs font-semibold text-stone-400 hover:text-amber-700"
          data-testid="admin-view-site-link"
        >
          View site <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </nav>

      <div className="mt-10">
        <Outlet />
      </div>
    </div>
  );
}
